import React, { useEffect, useState } from "react";
import { sendEmail } from "../../services/emailService";
import { getDenominations } from "../../services/denominationService";
import { Denomination } from "../../interfaces/denomination";
import ToolTip from "../../components/component-tools/ToolTip";
import { ClientStatus } from "../../interfaces/clientStatus";
import ReactQuill from "react-quill";

const EmailSending = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [denomination, setDenomination] = useState("All");
  const [denominations, setDenominations] = useState<Denomination[]>([]);
  const [clientStatus, setClientStatus] = useState("Both");
  const [clientStatuses, setClientStatuses] = useState<ClientStatus[]>([]);

  useEffect(() => {
    const fetchDenominations = async () => {
      try {
        setLoading(true);
        const denominationsResponse = await getDenominations();
        const excludedDenominations = [2, 3, 4];
        const filteredDenominations = denominationsResponse
          .filter(
            (denomination) =>
              !excludedDenominations.includes(denomination.denominationID),
          )
          .map((denomination) => {
            if (denomination.denominationName === "Others") {
              return { ...denomination, denominationName: "All Denominations" };
            }
            return denomination;
          });
        setDenominations(filteredDenominations);

        setClientStatuses([
          { id: 1, statusName: "Both" },
          { id: 2, statusName: "Active" },
          { id: 3, statusName: "Inactive" },
        ]);
      } catch (error) {
        console.error("Error fetching bishops:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDenominations();
  }, []);

  const handleSend = async () => {
    if (!subject || !message) {
      setErrorMessage("Subject and message are required.");
      return;
    }

    try {
      setLoadingSpinner(true);
      setErrorMessage("");
      setSuccessMessage("");

      const formattedMessage = message.replace(/(?:\r\n|\r|\n)/g, "<br />");

      const response = await sendEmail(
        subject,
        formattedMessage,
        denomination,
        clientStatus,
      );

      if (response.success) {
        setSuccessMessage(
          `Successfully sent email to ${response.count} recipient${
            response.count === 1 ? "" : "s"
          }.`,
        );
        setSubject("");
        setMessage("");
      } else {
        setErrorMessage("Something went wrong while sending emails.");
      }
    } catch (err) {
      setErrorMessage("Failed to send request.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleEmailFieldChange = (value: string) => {
    setMessage(value);
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <div className="page-items-container" style={{ alignItems: "center" }}>
          <h1>Email Clients</h1>
        </div>
      </div>
      <div className="divider"></div>
      <p className="text-muted">
        This message will be sent to all client contacts with email
        notifications enabled.
      </p>

      <div className="mb-3">
        <label className="form-label">Subject</label>
        <input
          type="text"
          className="form-control"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Message</label>
        <ReactQuill
          theme="snow"
          value={message ?? ""}
          onChange={(content) => handleEmailFieldChange(content)}
          style={{ height: "300px", marginBottom: "50px" }}
        />
        <div
          style={{
            fontSize: "14px",
            marginTop: "4px",
            textAlign: "right",
          }}
        >
          {message.length ?? 0}/4000
        </div>
      </div>

      <div className="filter-body">
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Denomination:</label>
          <div className="field-input">
            <select
              className="form-control"
              value={denomination}
              onChange={(e) => setDenomination(e.target.value)}
            >
              <option value="">Select a Denomination</option>
              {denominations.map((denomination) => (
                <option
                  key={denomination.denominationID}
                  value={denomination.denominationName}
                >
                  {denomination.denominationName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="filter-body">
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Client Status:</label>
          <div className="field-input">
            <select
              className="form-control"
              value={clientStatus}
              onChange={(e) => setClientStatus(e.target.value)}
            >
              <option value="">Select a Client Status</option>
              {clientStatuses.map((status) => (
                <option key={status.id} value={status.statusName}>
                  {status.statusName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div
        style={{
          flex: "0 0 100%",
          paddingTop: "20px",
          display: "flex",
          gap: "10px",
        }}
      >
        <button className="oxbtn btn-purple" onClick={handleSend}>
          Send Email
        </button>
        <ToolTip
          tooltipText="If 'All Denominations' is selected as the denomination, email will be sent to all denominations."
          placement="right"
          className="info-icon-spec"
        ></ToolTip>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default EmailSending;
